import { useState } from 'react';
import { ChevronRight, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import { POST_EFFECTS } from '../../core/postfx/chain';
import { isVisible } from '../../core/params/types';
import { useStudio } from '../../store/studioStore';
import { ParamControl } from '../params/ParamControl';

/**
 * The post-processing chain.
 *
 * Listed in application order rather than alphabetically, because the order is the
 * pipeline: what a vignette darkens depends on everything drawn before it. The number
 * in front of each effect makes that order explicit instead of implied.
 */
export function PostFxPanel(): JSX.Element {
  const postFx = useStudio((s) => s.doc.postFx);
  const setPostEnabled = useStudio((s) => s.setPostEnabled);
  const setPostValue = useStudio((s) => s.setPostValue);
  const resetPostEffect = useStudio((s) => s.resetPostEffect);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const activeCount = POST_EFFECTS.filter((e) => postFx[e.id]?.enabled).length;

  return (
    <div className="px-3 pb-3">
      <div className="flex items-center gap-2 py-2.5">
        <span className="panel-label flex-1">Post-Processing</span>
        {activeCount > 0 && (
          <span className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[9px] text-accent-500">
            {activeCount} aktiv
          </span>
        )}
      </div>

      <div className="space-y-0.5">
        {POST_EFFECTS.map((effect, index) => {
          const entry = postFx[effect.id];
          const enabled = entry?.enabled === true;
          const expanded = open[effect.id] === true;

          return (
            <div
              key={effect.id}
              className={clsx('rounded-md transition-colors', enabled ? 'bg-ink-850' : 'bg-ink-850/40')}
            >
              <div className="flex items-center gap-1.5 px-1.5 py-1.5">
                <span className="w-3 shrink-0 text-right font-mono text-[9px] text-ink-600">{index + 1}</span>

                <button
                  type="button"
                  title={enabled ? 'Effekt ausschalten' : 'Effekt einschalten'}
                  onClick={() => setPostEnabled(effect.id, !enabled)}
                  className={clsx(
                    'relative h-[14px] w-[24px] shrink-0 rounded-full transition-colors',
                    enabled ? 'bg-accent-500' : 'bg-ink-700',
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-[2px] h-[10px] w-[10px] rounded-full bg-ink-950 transition-all',
                      enabled ? 'left-[12px]' : 'left-[2px]',
                    )}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [effect.id]: !o[effect.id] }))}
                  className="flex min-w-0 flex-1 items-center gap-1 text-left"
                  title={effect.description}
                >
                  <span className={clsx('truncate text-[11px]', enabled ? 'text-ink-100' : 'text-ink-400')}>
                    {effect.name}
                  </span>
                  <ChevronRight
                    size={11}
                    className={clsx('shrink-0 text-ink-600 transition-transform', expanded && 'rotate-90')}
                  />
                </button>

                {expanded && (
                  <button
                    type="button"
                    className="ghost-button h-5 w-5 shrink-0 p-0"
                    title="Parameter zurücksetzen"
                    onClick={() => resetPostEffect(effect.id)}
                  >
                    <RotateCcw size={10} />
                  </button>
                )}
              </div>

              {expanded && (
                <div className={clsx('px-2 pb-2', !enabled && 'opacity-50')}>
                  <p className="pb-1 text-[10px] leading-relaxed text-ink-500">{effect.description}</p>
                  {effect.params
                    .filter((p) => isVisible(p, entry?.values ?? {}))
                    .map((param) => (
                      <ParamControl
                        key={param.key}
                        param={param}
                        value={entry?.values?.[param.key]}
                        bulk={undefined}
                        onChange={(v) => setPostValue(effect.id, param.key, v)}
                        onBulkChange={() => undefined}
                        onToggleLock={() => undefined}
                        onRandomize={() => undefined}
                      />
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
